import React from 'react';
import { Footnote, FootnoteRef, FootNoteRefProps_t } from '../components/FootNote';
import { BlockMath, InlineMath } from 'react-katex';
import TermsOfService from '../assets/terms_of_service.png';
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import SafetyFilter from '../assets/SafetyClassifier.svg';
import SafetySteerFalsePositive from '../assets/Safety Steer False Positives.svg';
import SafetySteerTruePositiveHarmfulPrompt from '../assets/Safety_Steer_True_Positive_Harmful_Prompt.svg';
import SafetySteerTruePositiveHarmlessPrompt from '../assets/Safety_Steer_True_Positive_Harmless_Prompt.svg';
import AvoidRobot from '../assets/avoid_robot.svg';
import Projection from '../assets/projection.svg';
import Approximation from '../assets/avoid set approximation.svg';
import { sample_1_file, sample_2_file, sample_3_file, sample_4_file, sample_5_file, sample_6_file } from '../Outputs';
import { useFileContents } from '../Utils';

export const SafeSteer: React.FC<{}> = () => {

    const sample_1 = useFileContents(sample_1_file as any);
    const sample_2 = useFileContents(sample_2_file as any);
    const sample_3 = useFileContents(sample_3_file as any);
    const sample_4 = useFileContents(sample_4_file as any);
    const sample_5 = useFileContents(sample_5_file as any);
    const sample_6 = useFileContents(sample_6_file as any);

    // These footnotes are a little hacky. If you want your footnote to be in order,
    // you have to add it to this list in the order you want it.
    const references: Array<FootNoteRefProps_t> = React.useMemo(() => [
        { id: "Dario_1", text: "Section 2. A surprising and terrible empowerment - Misuse for destruction ", ref: "https://darioamodei.com/essay/the-adolescence-of-technology" },
        { id: "ChatGPT", ref: "https://www.bbc.com/news/articles/cgerwp7rdlvo" },
        { id: "ChatGPT2", ref: "https://www.cbsnews.com/news/open-ai-microsoft-sued-chatgpt-murder-suicide-connecticut/" },
        { id: "ChatGPT3", ref: "https://www.cbsnews.com/news/chatgpt-lawsuit-colordo-man-suicide-openai-sam-altman/" },
        { id: "Carlini", ref: "https://nicholas.carlini.com/writing/2025/are-llms-worth-it.html" },

        { id: "classifiers++", ref: "https://arxiv.org/pdf/2601.04603" },

        { id: "activations", ref: "https://arxiv.org/pdf/1610.01644" },
        { id: "representation", ref: "https://arxiv.org/pdf/2310.01405" },
        { id: "logic", ref: "https://arxiv.org/pdf/2212.03827" },
        { id: "stakes", ref: "https://arxiv.org/pdf/2506.10805" },
        { id: "beyond linear", ref: "https://arxiv.org/pdf/2509.26238" },

        { id: "steering 2022", ref: "https://arxiv.org/pdf/2205.05124" },
        { id: "instruction steering", ref: "https://arxiv.org/pdf/2410.12877" },
        { id: "steering refusal", ref: "https://arxiv.org/pdf/2411.11296" },
        { id: "analyzing steering", ref: "https://arxiv.org/pdf/2407.12404v1" },
        { id: "reliable steering analysis", ref: "https://www.semanticscholar.org/paper/Towards-Reliable-Evaluation-of-Behavior-Steering-in-Pres-Ruis/a4bf64d6b3e4f13b2c8b5de2b5e6d568a74302e5" },
        { id: "refusal geometery", ref: "https://arxiv.org/pdf/2502.17420" },

        { id: "know it", ref: "https://en.wikipedia.org/wiki/I_know_it_when_I_see_it" },

        { id: "CBF", text: "page 4 section Safety Critical Control", ref: "https://arxiv.org/pdf/1903.11199"},
        { id: "CBF discrete time", text: "extension of CBF to discrete time", ref: "https://arxiv.org/pdf/2007.11718"},

        { id: "code", ref: "https://github.com/neil-ski/safe_steering" },

        { id: "shieldgemma-2b", ref: "https://huggingface.co/google/shieldgemma-2b" },
        { id: "LLM-LAT/harmful-dataset", ref: 'https://huggingface.co/datasets/LLM-LAT/harmful-dataset' },


        { id: "experiment_1", ref: "https://github.com/neil-ski/safe_steering/blob/master/experiments/experiment_1.py" },
        { id: "abliterated", ref: "https://huggingface.co/huihui-ai/Llama-3.2-3B-Instruct-abliterated" },
        { id: "experiment_2", ref: "https://github.com/neil-ski/safe_steering/blob/master/experiments/experiment_2.py" },
        { id: "experiment_3", ref: "https://github.com/neil-ski/safe_steering/blob/master/experiments/experiment_3.py" },

        { id: "Phi4Judge", ref: "https://huggingface.co/microsoft/Phi-4-mini-instruct" },
        { id: "experiment_4", ref: "https://github.com/neil-ski/safe_steering/blob/master/experiments/experiment_4.py" },

        { id: 'databricks/databricks-dolly-15k', ref: "https://huggingface.co/datasets/databricks/databricks-dolly-15k" },
        { id: "experiment_5", ref: "https://github.com/neil-ski/safe_steering/blob/master/experiments/experiment_5.py"},

        { id: "relu1", ref: "https://www.cs.toronto.edu/~hinton/absps/reluICML.pdf" },

        { id: "DQN", ref: "https://arxiv.org/pdf/1312.5602" },
        { id: "SAC", ref: "https://arxiv.org/pdf/1801.01290" },

        { id: "reach", ref: "https://arxiv.org/pdf/2210.08339" },
        { id: "reach2", ref: "https://arxiv.org/pdf/2303.10513" },
        // { id: "halfspace projection", text: "8.1.1 Projecting a point on a convex set, page 412 of PDF, 398 of book", ref: "https://stanford.edu/~boyd/cvxbook/bv_cvxbook.pdf" },
        { id: "Polyhedra", text: "2.2.4 Polyhedra, page 45 of PDF, 31 of book", ref: "https://stanford.edu/~boyd/cvxbook/bv_cvxbook.pdf" },
        { id: "geocert", ref: "https://arxiv.org/pdf/1903.08778" },
        // { id: "multi agent", text: "start at 3:28:51", ref: "https://www.youtube.com/live/HzINynTTwiA?t=12531" },
        { id: "RLHF_Book", ref: "https://arxiv.org/pdf/2504.12501" },
        { id: "finegrained", ref: "https://arxiv.org/pdf/2306.01693" },
    ], []);

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
        let index = references.findIndex(ref => ref.id === id);
        if (index === -1) {
            throw Error(`could not find reference: ${id} in list`);
        }
        return index + 1;
    }, [references]);

    return (
        <main>
            <h1>Minimum Safe Steer: Reducing Safety Filter Product Impact with Vector Steering Based on Activation Probes</h1>
            <p>
                Large Language Models (LLMs) can produce harmful content. One such harm is outputting how to manufacture chemical, biological, radiological,
                or nuclear weapons(CBRN)<Footnote f={f} id="Dario_1" /> to
                people who would otherwise not have the knowledge to do so.
                If those threats sound too outlandish, there are examples of
                chatbots encouraging people to take the lives of themselves and others.<Footnote id="ChatGPT" f={f} /> <Footnote id="ChatGPT2" f={f} /> <Footnote id="ChatGPT3" f={f} />I suggest
                watching this talk<Footnote f={f} id="Carlini" /> on the real harms the output of LLMs can cause today.
            </p>

            <h4>The Product Reality of Safety</h4>
            <div style={{ textAlign: 'center' }}>
                <img src={SafetyFilter} alt="Safety Filter" style={{ width: '70%' }} />
            </div>

            <p>
                A common way to mitigate that risk is to use a safety filter that prevents the user seeing harmful content. The safety filter has a
                classifier that determines if the output is unsafe and if it is, it will display a static message such as "this violates our terms of service."
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={TermsOfService} alt="Terms of Service Violation" style={{ width: '100%' }} />
            </div>

            <p>
                In any classifier there is a tradeoff between false positives and false negatives.
                Too many false positives (like the one shown above) could make our product unusable.
            </p>
            <p>
                As safety researchers we cannot ignore the practical realities of building safe frontier models.
                If our safety measures don't integrate smoothly with our product, then they won't be implemented.
            </p>
            <p>
                The model provider will have a target rate of positives that balances
                false positives (product) and false negatives (safety). For example
                Anthropic's recent paper writes "We calibrated classifier thresholds to correspond to a 0.1% refusal rate on WildChat" and observed a "0.05% refusal rate on production traffic" <Footnote id="classifiers++" f={f} />.
            </p>
            <h4>Goal: Closest Safe Response</h4>
            <p>
                What if we could reduce the product impact of false positives and ease this tension between product and safety? This would allow us to deploy more sensitive safety classifiers
                and improve the safety of our system.
            </p>
            <p>
                So I'm going to propose a goal
            </p>
            <div style={{
                textAlign: "center",
                margin: "1em 0"
            }}>
                <p style={{
                    fontWeight: "bold",
                    backgroundColor: "#f0f0f0",
                    padding: "0.5em 0.4em",
                    borderRadius: "4px"
                }}>
                    Goal: Given an unsafe response, find the closest safe response
                </p>
            </div>
            <p>
                There are three cases to consider. The first is false positives. Here we hope that we can reword the response such that it passes
                the safety filter but still is close enough to the original to provide value.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={SafetySteerFalsePositive} alt="Safety Steer False Positive" style={{ width: '100%' }} />
            </div>
            <p>
                The second case is true positives with a harmful prompt. Here we want to make sure we still give a safe response but we don't care about
                providing value to the malicious attacker.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={SafetySteerTruePositiveHarmfulPrompt} alt="Safety Steer True Positive Harmful Prompt" style={{ width: '100%' }} />
            </div>
            <p>
                The third case is true positives with a harmless prompt.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={SafetySteerTruePositiveHarmlessPrompt} alt="Safety Steer True Positive Harmless Prompt" style={{ width: '100%' }} />
            </div>
            <p>That seems ideal, but how do we implement this?</p>
            <h4>Background: Activation Probing</h4>
            <p>
                In a deep learning model, its activations <InlineMath math="\phi" /> reflect its internal state. These activations can be probed to better interpret
                the model.<Footnote id="activations" f={f} /> <Footnote id="representation" f={f} />
            </p>
            <p>
                This has been applied to LLMs to analyze internal logical
                consistency<Footnote id="logic" f={f} /> and predict harmful
                conversations<Footnote id="stakes" f={f} /> and prompts<Footnote id="beyond linear" f={f} />.
            </p>
            <p>
                Surprisingly a simple linear model can be trained to predict whether an LLM will output harmful content from these activations. Anthropic's linear probe was able to
                stop greater than 98% of jailbreak attacks<Footnote id="classifiers++" f={f} />. The theoretical justification is that the activations represent
                a space of language representations that can be leveraged for predicting harmfulness.
            </p>
            <h4>Background: Linear Activation Probing</h4>
            <p>
                There is interest in linear probes due to their low computational overhead. Safety classifiers in a production system are evaluated many times per
                response, so chatbot providers aim to lower the cost: "the probe is thus several orders of magnitude cheaper than
                a small external classifier, rendering its marginal cost for per-token prediction effectively zero."
            </p>
            <p>
                I'm interested in them for their analytical simplicity but the low cost is a great benefit.
            </p>
            <h4>Background: Vector Steering</h4>
            <p>
                There is also work in adjusting activations with so-called <em>Steering Vectors</em><Footnote id="steering 2022" f={f} />. For example to make the LLM's answer more cheerful, a
                steering vector pointing in the direction of "happiness" in the latent space of the activation could be added. Steering can be used to improve the model's instruction following<Footnote id="instruction steering" f={f} />
                and to make the model refuse unsafe requests<Footnote id="steering refusal" f={f} />.
                However some research indicates these steering techniques can be
                unreliable<Footnote id="analyzing steering" f={f} /> <Footnote id="reliable steering analysis" f={f} /> in producing the desired effect
                and refusal steering can degrade performance on safe requests<Footnote id="refusal geometery" f={f} />.
            </p>
            {/* <h4>Production Reality vs Research Experiment</h4>
            <p>When frontier models are exposed to the public, it is through the interface of an API or chatbot UI. In these setups there more than one model being run, often there is a
                classifier. But some research on jailbreaks assume the classifier isn't there. Attacks that a classifier would catch will work without it there. Also can a classifier be used
                to improve.
            </p>
            <p>
                Quote: "single LLMs don't necssarily do the things we want them to do and often time we do want to plug in multi-agent systems instead. Yet there seems to be a clear focus
                on making one LLM do everything in a lot of the development and machine learning community... Practically speaking when you interact with chatgpt.com you're not
                interacting with one LLM, you're interacting with multiple different LLMs [for example one] filters for content safety... We're really focused on evaluating the LLM
                by itself but I think a lot of us should be moving towards this full system." Maarten Sap CMU professor at IASEAI'26.<Footnote id="multi agent" f={f} />
            </p> */}
            <h4>Tricky Definition of Safety</h4>
            <p>
                In robotics and controls safety, we define a subset of the state space as
                the <em>Avoid Set</em> <InlineMath math="\mathcal{A}" />. This is the part of the state space
                where entering counts as a safety violation. For example if we were driving a car, an obstacle would be in the Avoid Set.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={AvoidRobot} alt="Avoid Set for Robot" style={{ width: '40%' }} />
            </div>
            <p>
                What's tricky about language is we don't have an obvious Avoid Set we can define.
                In language we can't write out every sentence that we would like to prevent our (LLM) from generating.
            </p>
            <h4>Harmful Content: I Know It When I See It</h4>
            <p>
                It is famously difficult to define exactly what qualifies as inappropriate content.
                In 1961 a Supreme Court Justice used the phrase "I Know It
                When I See It" to "describe his threshold test for obscenity"<Footnote id="know it" f={f} />.
            </p>
            <p>
                Going forward
                I'm going to assume that a machine learning model that has been trained to classify harmful content is the best definition we have.
            </p>
            <h4>Defining the Avoid Set Based on Linear Activation Probes</h4>
            <p>Let's call <InlineMath math="\phi^{l}" /> the activation of layer <InlineMath math="l" /> of our network and then we can define our linear probe</p>
            <BlockMath math="p(\text{harmful}|\phi^l) = \sigma(w^{T}\phi^l+ b)" />
            <p>
                Where <InlineMath math="\sigma(x)=\frac{1}{1+e^{-x}}" /> is the Sigmoid function to map the reals to <InlineMath math="(0,1)" /> to
                create a probability distribution. <InlineMath math="w \in \mathbb{R}^{n}" /> and <InlineMath math="b \in \mathbb{R}" /> are
                the learnable parameters of our linear probe classifier.
            </p>
            <p>
                The probability threshold for flagging an output as unsafe is <InlineMath math="\tau_{\text{unsafe}}" />
            </p>
            <BlockMath math="f_{\text{safety classifier}}(\phi^l) =
\begin{cases}
\text{unsafe} & \text{if } \sigma(w^{T}\phi^l+ b) > \tau_{\text{unsafe}} \\
\text{safe} & \text{else }
\end{cases}"/>
            <p>Now we can find the avoid set</p>
            <BlockMath math="\mathcal{A} = \{ \phi^l \ : \ \sigma(w^{T}\phi^l+ b) > \tau_{\text{unsafe}} \}" />
            <p>With a little algebra:</p>
            <BlockMath math="\sigma(w^{T}\phi^l+ b) > \tau_{\text{unsafe}}" />
            <BlockMath math="\frac{1}{1+e^{-(w^{T}\phi^l+ b)}} > \tau_{\text{unsafe}}" />
            <BlockMath math="\frac{1}{\tau_{\text{unsafe}}} > 1+e^{-(w^{T}\phi^l+ b)}" />
            <BlockMath math="\frac{1}{\tau_{\text{unsafe}}} - 1 > e^{-(w^{T}\phi^l+ b)}" />
            <BlockMath math="\ln(\frac{1}{\tau_{\text{unsafe}}} - 1) > -(w^{T}\phi^l+ b)" />
            <BlockMath math="-\ln(\frac{1}{\tau_{\text{unsafe}}} - 1) < w^{T}\phi^l+ b" />
            <BlockMath math="-\ln(\frac{1}{\tau_{\text{unsafe}}} - 1) - b < w^{T}\phi^l" />
            <p>Note that <InlineMath math="\tau_{\text{safe}}" />, <InlineMath math="b" />, and <InlineMath math="w" /> are all known. So our Avoid Set is just a half space defined by a hyperplane</p>
            <BlockMath math="\mathcal{A} = \{ \phi^l \ : \ w^{T}\phi^l > - \ln(\frac{1}{\tau_{\text{unsafe}}} - 1) - b \}" />
            <p>
                I've embedded an interactive 3D graph below. The purple curve defines the probability estimate a
                given input is unsafe and the red half space is the unsafe set for a given <InlineMath math="\tau_{\text{unsafe}}" /> and blue is the safe set. Try adjusting <InlineMath math="\tau_{\text{unsafe}}" />.
            </p>
            <iframe
                src="https://www.desmos.com/3d/ls2dnm2ed4?embed"
                width="100%"
                height="700px"
                style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                sandbox="allow-scripts allow-same-origin"
                title='desmos linear'
            ></iframe>
            <h4>Minimum Safe Steer</h4>
            <p>Let's return to our original problem. We want to find the <em>smallest</em> change we can make to the activation to keep it out of the Avoid Set.</p>
            <p>
                We care about it being the <em>smallest</em> change because we want it to be similar in meaning to the original response.
                In activation space, vectors that correspond to text with similar meanings are close together.
                In the case of a false positive, choosing a similar activation
                should hopefully result in a response that has low product impact.
            </p>
            <BlockMath math="\bold{u}^*_{\text{change}} = \min_{\bold{u}_{\text{change}}} \|\bold{u}_{\text{change}} \|_{2} \\ s.t. \ w^{T}(\phi^l + \bold{u}_{\text{change}}) \leq  - \ln(\frac{1}{\tau_{\text{unsafe}}} - 1) - b " />
            <p>This is a convex quadratic program that we could throw into a solver but we can actually find an analytic solution.</p>
            <p>If the activation is outside of the Avoid Set, we don't have to do anything. If it's inside of it, we just project the point onto the boundary.</p>
            <BlockMath math="\phi^l + \bold{u}^{*}_{\text{change}} =
\begin{cases}
\phi^l - \frac{w^{T}\phi^l + \ln(\frac{1}{\tau_{\text{unsafe}}} - 1) + b}{\|w\|^{2}}w & \text{if } \sigma(w^{T}\phi^l+ b) > \tau_{\text{unsafe}} \\
\phi^l & \text{else}
\end{cases}"/>
            <p>Note that unlike some other steering approaches that always add a constant vector, we dynamically change the activation and if we deem it safe, don't change it at all.</p>
            <div style={{ textAlign: 'center' }}>
                <img src={Projection} alt="Projection of unsafe activations onto boundary" style={{ width: '50%' }} />
            </div>
            <h4>How to Use This at Inference Time: Adjusting Unsafe Responses</h4>
            <p>
                In their recent paper, Anthropic uses a linear activation probe as the
                first stage of a multi-stage classifier.<Footnote id="classifiers++" f={f} />
            </p>
            <p>
                I've written a simplified version of it as <InlineMath math="\pi^l_{\text{two stage safety filter}}(\text{prompt})" /> where <InlineMath math="l" /> is the
                layer number of the activation we are probing and our LLM has <InlineMath math="L" /> layers total.
            </p>

            <BlockMath math="\begin{array}{l}
                    \pi^l_{\text{two stage safety filter}}(\text{prompt}): \\
                    \ \ \ \ [\phi^{1} \ ... \ \phi^{l} \ ... \ \phi^{L} ] \leftarrow \text{LLM}(\text{prompt}) \\
                    \ \ \ \  \text{original response} \leftarrow \text{sample}(\phi^{L}) \\
                    \ \ \ \ \textbf{if} \ \phi^{l} \notin \mathcal{A}  \\
                    \ \ \ \ \ \ \ \ \ \  \textbf{return} \ \text{original response}\\
                    \ \ \ \ \textbf{else if} \ f_{\text{safety}}(\text{prompt}, \ \text{original response}) = \text{unsafe}\\
                    \ \ \ \ \ \ \ \ \ \ \ \textbf{return} \ \text{terms of service violation message} \\
                    \ \ \ \ \textbf{else}\\
                    \ \ \ \ \ \ \ \ \ \ \ \textbf{return} \ \text{original response} \\



                    \end{array}"
            />
            <p>
                In their approach, the first stage classifier has a low false negative rate at the expense of a higher false positive rate.
                If it flags activations as unsafe, the model output is sent to a more expensive but more precise output classifier <InlineMath math="\ f_{\text{safety}}" /> for
                a final decision.
            </p>
            <p>
                I would argue that once <InlineMath math="\ f_{\text{safety}}" /> flags the response as unsafe, we should try to find the closest safe response.
            </p>
            <BlockMath math="\begin{array}{l}
                    \pi^l_{\text{minimum safe steer}}(\text{prompt}): \\
                    \ \ \ \ [\phi^{1} \ ... \ \phi^{l} \ ... \ \phi^{L} ] \leftarrow \text{LLM}(\text{prompt}) \\
                    \ \ \ \  \text{original response} \leftarrow \text{sample}(\phi^{L}) \\
                    \ \ \ \ \textbf{if} \ \phi^l \notin \mathcal{A}  \\
                    \ \ \ \ \ \ \ \ \ \  \textbf{return} \ \text{original response}\\
                    \ \ \ \ \textbf{else if} \ f_{\text{safety}}(\text{prompt}, \ \text{original response}) = \text{unsafe}\\
                    \ \ \ \ \ \ \ \ \ \  \phi^{l}_{\text{steered}} = \phi^{l} + \bold{u}_{\text{change}}^{*}\\
                    \ \ \ \ \ \ \ \ \ \  [\phi^{l+1}_{\text{steered}} \ ... \ \phi^{L}_{\text{steered}}] = \text{LLM activation after}(\phi^{l}_{\text{steered}})\\
                    \ \ \ \ \ \ \ \ \ \  \text{steered response} \leftarrow \text{sample}(\phi^{L}_{\text{steered}}) \\
                    \ \ \ \ \ \ \ \ \ \ \textbf{if} \ f_{\text{safety}}(\text{prompt}, \ \text{steered response}) = \text{safe}\\
                    \ \ \ \ \ \ \ \ \ \ \ \ \ \ \textbf{return} \ \text{steered response} \\
                    \ \ \ \ \ \ \ \ \ \ \textbf{else} \\
                    \ \ \ \ \ \ \ \ \ \ \ \ \ \ \textbf{return} \ \text{terms of service violation message} \\
                    \end{array}"
            />
            <p>
                Note that we have to re-compute all of the activations after layer <InlineMath math="l" /> and
                run the <InlineMath math="f_{\text{safety}}" /> classifier again. We also have to re-compute all activations 
                for all following tokens of the response (more details in next section).
            </p>
            <p>
                If we believe that our linear
                probe's approximation of the Avoid Set <InlineMath math="\mathcal{A}" /> is an over-approximation of the Avoid Set
                of the <InlineMath math="f_{\text{safety}}" /> classifier, then we could justify skipping running it again. Since we are using it
                as the first stage of our safety filter, we probably do think it is an over-approximation. But my results indicate 
                that steering performance is worse than classification performance, so I would recommend keeping it.
            </p>
            
            <h4>Token Details</h4>
            <p>
                So far I have simplified things a little by saying there is only one activation at 
                layer <InlineMath math="l"/> called <InlineMath math="\phi^{l}"/>. In reality a LLM must repeatedly predict
                the next token multiple times to generate a response. So in a response of length <InlineMath math="T"/> the 
                neural network will be evaluated <InlineMath math="T"/> times and therefore 
                layer <InlineMath math="l"/> will be evaluated <InlineMath math="T"/> times.
            </p>
            <p>
                We can subscript the activation with its position in the response and 
                we get <InlineMath math="\{ \phi^{l}_{1} , \phi^{l}_{2} \ ... \ \phi^{l}_{T}\}"/> a trajectory over 
                the activations. We would like to keep the entire trajectory out of the Avoid Set. 
            </p>
            <p>
                This problem looks a lot like one from Control Barrier Functions. Specifically 
                the problem of trying to change our control as little as possible 
                while keeping it safe.<Footnote id="CBF" f={f}/> <Footnote id="CBF discrete time" f={f}/>
            </p>
            <p>
                From this perspective, our problem is relatively easy because the dynamics are such that we can just change
                the state of <InlineMath math="\phi_{t}^{l}"/> directly. In other systems the dynamics might be such that 
                we can only control the second time derivative (double integrator) or are fighting with a disturbance. 
            </p>
            <p>
                So when implementing Minimum Safe Steer, we check if <InlineMath math="\phi_{t}^{l}"/> is 
                in <InlineMath math="\mathcal{A}"/> for all <InlineMath math="t\in \ \{1,2 \ ... \ T\}"/>. So we check if we need to steer on every token. But that doesn't change the math
                so I'm going to omit the subscripts denoting the token position from now on.
            </p>
            <h4>Preliminary Results</h4>
            <p>
                All code is available here.<Footnote id="code" f={f} />
            </p>
            <p>
                Note that when I laid out the three cases before, I referred to them as false positives, true positives with harmful prompt, 
                and true positive with harmless prompt. But in production, we don't know if we have a true positive or a false positive, all we know 
                is that our classifier flagged this output as unsafe. Similarly we don't know whether a prompt is unsafe or safe. We can add a classifier 
                but that will have its own errors.
            </p>
            <p>
                Constructing false positives is difficult. Additionally there's a level of ambiguity. If a model refuses to give specific information about how to construct a weapon but gives general information 
                about how they work that one could find in Wikipedia, is that harmful? For the safety judge I used ShieldGemma-2b<Footnote id="shieldgemma-2b" f={f} />, I've seen it mark
                general information as both safe and unsafe.
            </p>
            <p>
                Rather than try that, I have 3 steering cases: 1. steering unsafe responses to unsafe prompts 
                2. steering safe responses to unsafe prompts and 3. steering safe responses to safe prompts. 
            </p>
            <p>
                I used ShieldGemma-2b<Footnote id="shieldgemma-2b" f={f} /> to judge the safety of responses and I used the subset
                of the LLM-LAT/harmful-dataset<Footnote id="LLM-LAT/harmful-dataset" f={f} /> where ShieldGemma correctly marked
                the chosen response as safe and the rejected response as unsafe. Code here.<Footnote id="experiment_1" f={f} />
            </p>
            <p>
                Using Llama-3.2-3B-Instruct, the model responds safely to 100% of prompts so I instead used a so-called "abliterated"
                model<Footnote id="abliterated" f={f} /> where the safety guardrails have been fine-tuned out. This model responds unsafely to
                537/1306 of prompts.
            </p>
            <p>
                I extracted activations at layers [6, 8, 10, 12, 14] of the final token of the prompt. This represents
                the activation just before we generate the first token of the response. Code here.<Footnote id="experiment_2" f={f} /> I normalized the data and fit
                a linear model and got these results. Code here.<Footnote id="experiment_3" f={f} />
            </p>
            <BlockMath math={String.raw`
    \begin{array}{|l|c|c|c|r|}
        \hline
        \text{Layer} & \text{Train False Positive} & \text{Train False Negative} & \text{Test False Positive} & \text{Test False Negative}  \\
        \hline
        6 & 6.88\% & 40.33\% & 10.05\% & 47.47\% \\
        \hline
        8 & 7.78\% & 37.47\% & 06.22\% & 39.83\% \\
        \hline
        10 & 7.44\% & 36.04\% & 08.61\% & 41.53\% \\
        \hline
        12 & 9.36\% & 26.25\% & 09.57\% & 33.90\% \\
        \hline
        14 & 9.58\% & 21.72\% & 12.44\% & 26.27\% \\
        \hline
    \end{array}
`} />
            <p>
                Then I used the Minimum Safe Steer with the three best-performing layers [10, 12, 14] and various <InlineMath math="\tau_{\text{unsafe}}" /> probability cutoffs. Considering
                the high false negative rates, I chose a very low threshold. Since the logistic regression data was normalized, we have to normalize it, project and then de-normalize it.
                I used the test set that the logistic regression model was not trained on.
                Code here.<Footnote id="experiment_3" f={f} />
            </p>
            <BlockMath math={String.raw`
    \begin{array}{|l|c|c|r|}
        \hline
        \text{Layer} & \tau_{\text{unsafe}} & \text{Unsafe responses steered to safe} & \text{Percent of responses w/ some steering}\\ 
        \hline
        10 & 10^{-20} & 73.3\% & 100.0\%\\ 
        \hline
        10 & 10^{-10} & 40.0\% & 100.0\%\\ 
        \hline
        10 & 10^{-5} & 21.9\% &  100.0\%\\
        \hline
        10 & 10^{-4} & 16.2\% &  100.0\%\\
        \hline
        10 & 10^{-3} & 16.2\% & 100.0\% \\
        \hline
        10 & 10^{-2} & 10.5\% & 100.0\% \\
        \hline
        10 & 10^{-1} &  1.0\% & 100.0\% \\
        \hline
        10 & 0.500 &  2.9\% & 99.0\% \\
        \hline
        \hline
        12 & 10^{-20} & 100.0\% &  100.0\%  \\
        \hline
        12 & 10^{-10} & \textbf{80.0\%} &  100.0\%  \\
        \hline
        12 & 10^{-5} & 45.7\% &  100.0\%  \\
        \hline
        12 & 10^{-4} & 41.0\% &  100.0\%  \\
        \hline
        12 & 10^{-3} & 32.4\% & 100.0\% \\
        \hline
        12 & 10^{-2} & 19.0\% & 100.0\% \\
        \hline
        12 & 10^{-1} &  5.7\% & 100.0\% \\
        \hline 
        12 & 0.500 &  2.9\% & 100.0\% \\
        \hline
        \hline
        14 & 10^{-20} & 87.6\% & 100.0\%\\ 
        \hline
        14 & 10^{-10} & 65.7\% & 100.0\%\\ 
        \hline
        14 & 10^{-5} & 47.6\% &  100.0\%  \\
        \hline
        14 & 10^{-4} & 37.1\% &  100.0\%  \\
        \hline
        14 & 10^{-3} & 27.6\% & 100.0\% \\
        \hline
        14 & 10^{-2} & 24.8\% & 100.0\% \\
        \hline
        14 & 10^{-1} & 12.4\% & 100.0\% \\
        \hline
        14 & 0.500 &  1.9\% & 100.0\% \\
        \hline
    \end{array}
`} />
            <p>
                Note that for every probe and <InlineMath math="\tau_{\text{unsafe}}" /> tested, 
                decreasing <InlineMath math="\tau_{\text{unsafe}}" /> increased the chance our steering was safe.
                That matches our expectation.
            </p>
            <p>Let's take a look at some of the responses. The scores could be safer because they are garbled messes. We want to make sure they are actually refusals.</p>

            <CodeMirror
                value={sample_1}
                extensions={[EditorView.editable.of(false)]}
                height="300px"
                theme={'dark'}
            />
            With the highest threshold <InlineMath math="\tau_{\text{unsafe}} = 1E-20"/> and layer 12, 100% of unsafe prompts are refused.
            <CodeMirror
                value={sample_2}
                extensions={[EditorView.editable.of(false)]}
                height="300px"
                theme={'dark'}
            />
            <p>
                Notice how sometimes it seems to be about to give out 
                harmful information "However, I can provide general information 
                about the importance of safety and the proper handling of explosives."
                and then refuses to do so "**DO NOT use explosives.**" Because we steer on every token in the response,
                this matches what we would expect. As the response starts to near unsafe activations, it is steered back 
                to a safe response. Neat!
                </p>
            <p>
                Another thing I notice is how the refusals are still specific to the prompt. They are not an
                out-of-context "No", but rather "I can't help with creating a website that sells illegal drugs." 
                This aligns with our earlier hypothesis that by steering to the closest safe activation, the resulting 
                response will remain relevant to the original context.
            </p>
            <p>
                Next I tried steering safe responses from the test set to simulate how we deal with false positives on harmful prompts and 
                used microsoft/Phi-4-mini-instruct<Footnote id="Phi4Judge" f={f} /> as a
                judge of response's quality. Code here.<Footnote id="experiment_4" f={f} />
            </p>
            <BlockMath math={String.raw`
                \begin{array}{|l|c|c|c|r|}
                    \hline
                    \text{Layer} & \tau_{\text{unsafe}} & \text{Safe Responses} & \text{Percent of responses w/ some steering} & \text{Mean Phi4Judge Score}\\
                    \hline
                    \text{Baseline no steer} & \text{N/A} & 100\% & 0\% & 0.75 \\
                    \hline
                    \hline
                    10  & 10^{-20}  &  97.7\% & 100.0\% & 0.74\\
                    \hline
                    10  & 10^{-10}  &  96.0\% & 100.0\% & 0.81\\
                    \hline
                    10  & 10^{-5}  &  95.4\% & 100.0\% & 0.71\\
                    \hline
                    10 & 10^{-4} &  97.7\% & 100.0\% & 0.77\\
                    \hline
                    10 & 10^{-3} & 96.6\% & 100.0\% & 0.73\\
                    \hline
                    10 & 10^{-2} & 95.4\% &  98.3\% & 0.71\\
                    \hline
                    10 & 10^{-1} & 94.9\% &  64.0\% & 0.73\\
                    \hline
                    10 & 0.500 & 99.4\% &   8.0\% & 0.76\\
                    \hline
                    \hline
                    12  & 10^{-20}  & 100.0\% & 100.0\% & 0.82\\
                    \hline
                    12  & 10^{-10}  &  \textbf{98.9\%} & 100.0\% & \textbf{0.90}\\
                    \hline
                    12  & 10^{-5}  &  96.6\% & 100.0\% & 0.91\\
                    \hline
                    12 & 10^{-4} &  97.7\% & 100.0\% & 0.84\\
                    \hline
                    12 & 10^{-3} & 96.0\% & 100.0\% & 0.74\\
                    \hline
                    12 & 10^{-2} & 96.6\% &  94.9\% & 0.73\\
                    \hline
                    12 & 10^{-1} & 98.3\% &  46.9\% & 0.73\\
                    \hline
                    12 & 0.500 & 98.3\% &   6.3\% & 0.74\\
                    \hline
                    \hline
                    14  & 10^{-20}  & 100.0\% & 100.0\% & 0.73\\
                    \hline
                    14  & 10^{-10}  &  97.1\% & 100.0\% & 0.82\\
                    \hline
                    14  & 10^{-5}  &  98.9\% & 100.0\% & 0.96\\
                    \hline
                    14 & 10^{-4} &  98.3\% & 100.0\% & 0.80\\
                    \hline
                    14 & 10^{-3} & 97.7\% & 100.0\% & 0.74\\
                    \hline
                    14 & 10^{-2} & 96.6\% &  99.4\% & 0.70\\
                    \hline
                    14 & 10^{-1} & 96.0\% &  39.4\% & 0.75\\
                    \hline
                    14 & 0.500 & 98.3\% &   7.4\% & 0.74\\
                    \hline
                \end{array}

            `}/>
            <p>
                Note that any values less than 100% for Safe Responses indicates that we steered some safe responses to unsafe. 
                So I think it's worth it to keep the safety classifier at the end of any steered output.
            </p>
            <p>
                I notice that as <InlineMath math="\tau_{\text{unsafe}}"/> is lowered, we steer more often which matches our expectation.
            </p>
            <p>
                I would hypothesize that the Phi-4 as a judge scores are higher because the steered 
                responses result in stronger refusals of unsafe prompts.
                Phi-4 is judging the helpfulness of the assistant on a scale from 1 to 10 and it likely has 
                trained to discourage harmful content. A small qualitative analysis of the outputs seems to support that hypothesis.
            </p>
            <CodeMirror
                value={sample_3}
                extensions={[EditorView.editable.of(false)]}
                height="300px"
                theme={'dark'}
            />
            <p>
                To test false positives on harmless prompts, I used 50 samples from the dataset 
                databricks/databricks-dolly-15k<Footnote id="databricks/databricks-dolly-15k" f={f}/>. 
                I filtered to responses where the unsteered response was judged safe. Code here.<Footnote id="experiment_5" f={f}/>
            </p>
                <BlockMath math={String.raw`
                \begin{array}{|l|c|c|c|r|}
                    \hline
                    \text{Layer} & \tau_{\text{unsafe}} & \text{Safe Responses} & \text{Percent of responses w/ some steering} & \text{Mean Phi4Judge Score}\\
                    \hline
                    \text{Baseline no steer} & \text{N/A} & 100\% & 0\% & 7.34 \\
                    \hline
                    \hline
                    10 & 10^{-20} & 100.0\% & 100.0\% & 7.22 \\
                    \hline
                    10 & 10^{-10} & 100.0\% & 100.0\% & 7.62 \\
                    \hline
                    10 & 10^{-5} & 100.0\% & 100.0\% & 7.64 \\
                    \hline
                    10 & 10^{-4} & 100.0\% & 100.0\% & 7.62 \\
                    \hline
                    10 & 10^{-3} & 100.0\% & 100.0\% & 7.68\\
                    \hline
                    10 & 10^{-2} & 100.0\% & 100.0\% & 7.53\\
                    \hline
                    10 & 10^{-1} & 100.0\% & 100.0\% & 7.54\\
                    \hline
                    10 & 0.500 &  98.0\% &  98.0\% & 7.58\\
                    \hline
                    \hline
                    12 & 10^{-20} & 100.0\% & 100.0\% & 2.40 \\
                    \hline
                    12 & 10^{-10} & \textbf{100.0\%} & 100.0\% & \textbf{7.40} \\
                    \hline
                    12 & 10^{-5} & 100.0\% & 100.0\% & 7.62 \\
                    \hline
                    12 & 10^{-4} &  98.0\% & 100.0\% & 7.64 \\
                    \hline
                    12 & 10^{-3} & 100.0\% & 100.0\% & 7.54\\
                    \hline
                    12 & 10^{-2} &  98.0\% & 100.0\% & 7.50\\
                    \hline
                    12 & 10^{-1} &  98.0\% & 100.0\% & 7.54\\
                    \hline
                    12 & 0.500 & 100.0\% & 100.0\% & 7.43\\
                    \hline
                    \hline
                    14 & 10^{-20} & 100.0\% & 100.0\% & 2.32 \\
                    \hline
                    14 & 10^{-10} &  98.0\% & 100.0\% & 7.36 \\
                    \hline
                    14 & 10^{-5} &  98.0\% & 100.0\% & 7.64 \\
                    \hline
                    14 & 10^{-4} &  98.0\% & 100.0\% & 7.66 \\
                    \hline
                    14 & 10^{-3} &  98.0\% & 100.0\% & 7.50\\
                    \hline
                    14 & 10^{-2} & 100.0\% & 100.0\% & 7.63\\
                    \hline
                    14 & 10^{-1} &  98.0\% & 100.0\% & 7.52\\
                    \hline
                    14 & 0.500 & 100.0\% &  96.0\% & 7.44\\
                    \hline
                \end{array}
            `}/>
            <p>
                This matches our expectation that we can steer too much. If we look at prompts 
                and responses for layer 12 and layer 14 with <InlineMath math="\tau_{\text{unsafe}}=10^{-20}"/> we 
                see that they refuse safe requests.
            </p>
            <CodeMirror
                value={sample_4}
                extensions={[EditorView.editable.of(false)]}
                height="400px"
                theme={'dark'}
            />
            <p>
                Sometimes the coherency of the response degrades as well.
            </p>
            <CodeMirror
                value={sample_5}
                extensions={[EditorView.editable.of(false)]}
                height="600px"
                theme={'dark'}
            />
            <p>
                But for lower <InlineMath math="\tau_{\text{unsafe}}"/>, the model scores remarkably well. 
            </p>
            <CodeMirror
                value={sample_6}
                extensions={[EditorView.editable.of(false)]}
                height="600px"
                theme={'dark'}
            />
            <p>
                And we see that it produces coherent responses. 
            </p>
            <p>
                If we consider the three cases of steering unsafe responses to harmful prompts, safe responses to harmless prompts and 
                safe responses to harmless prompts layer 12 with <InlineMath math="\tau_{\text{unsafe}}=10^{-10}"/> performs remarkably well.
                I bolded its results in the table above.
            </p>
            <p>
                It reduces unsafe responses to harmful prompts by 80%, it steers safe responses on harmful prompts to safety to 98.9% of the time, and 
                it steers 100% of safe responses on harmless prompts to safety while performing slightly better than the unsteered model with a Phi4 score of
                7.40 compared to 7.34. Nice!
            </p>
            <p>
                I want to be clear these are preliminary results. They haven't been peer-reviewed and use a small dataset, 
                only one LLM, only one safety classifier and one judge. They suggest
                this might be a promising research direction but should not be treated as conclusive.
            </p>
            <p>
                That said, I'm excited that Minimum Safe Steer seems to handle this well.
                With a relatively small dataset of harmful prompts, we were able to train 
                linear probes that can be used to steer responses to what the safety 
                classifiers determines as safe without degrading 
                performance on harmless prompts. 
            </p>
            <h4>How to Use This at Training Time: Improving the LLM</h4>
            <p>
                Previous work on steering vectors has demonstrated that they can result in a degradation of
                the performance of the LLM.<Footnote id="steering refusal" f={f} /> <Footnote id="analyzing steering" f={f} /> <Footnote id="reliable steering analysis" f={f} /> <Footnote id="refusal geometery" f={f} />
            </p>
            <p>
                Minimum Safe Steer addresses this by only modifying unsafe vectors and modifying them as little as possible.
            </p>
            <p>
                Additionally we can train with Minimum Safe Steer in place. Note that because we have an analytical solution, we can take the gradient through it.
            </p>
            <p>
                Careful readers may note that our piecewise projection function is not differentiable at the boundary. ReLU is also not differentiable
                at a single point and has successfully been used to train large models.<Footnote id="relu1" f={f} />
            </p>
            <p>
                It's important to note that if the LLM weights change, the meaning of its activations could change and the classifier could become inaccurate.
                Changing both at the same time may lead to training instability. Reinforcement learning algorithms like DQN<Footnote id="DQN" f={f} /> and
                SAC<Footnote id="SAC" f={f} /> deal with a similar problem by holding the weights of one constant while training the other.
            </p>
            <h4>How to Use This at Training Time: Improving the Classifier</h4>
            <p>
                Fine-tuning the boundary of our classifier's Avoid Set can improve the classifier's performance.
            </p>
            <p>
                We could try to sample points around the boundary but the boundary is infinite. We'd like to sample points that are relevant to our
                user traffic.
            </p>
            <p>
                Using this approach of projecting harmful activations onto the boundary we can sample relevant points on the boundary. We can project unsafe activations
                to the boundary and check if the resulting response is safe or unsafe and use this as a signal to fine tune that boundary.
            </p>
            <h4>Over or Under Approximation of Avoid Set?</h4>
            <p>
                Our probe's estimate of the Avoid Set is of course not the true Avoid Set. So what happens when we misclassify parts of the set?
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={Approximation} alt="Comparison of approximated avoid set and true avoid set" style={{ width: '50%' }} />
            </div>
            <p>
                When we under-steer, the resulting response might not be safe. That's why we run the safety classifier <InlineMath math="\ f_{\text{safety}}" /> on the output
                before showing it to the user.
            </p>
            <p>
                When we over-steer, the resulting response may differ dramatically in meaning from the original.
            </p>
            <h4>More Complicated Classifiers</h4>
            <p>
                A linear classifier already works surprisingly well, what if we try to use a small ReLU neural network as our activation probe?
            </p>
            <p>
                Let's add a single layer that has one output so <InlineMath math="w_1\in \mathbb{R}^n" /> and <InlineMath math="b_1,w_1,b_2\in \mathbb{R}" />
            </p>
            <BlockMath math="p(\text{harmful}|\phi) = \sigma(w_2\text{ReLU}(w_1^{T}\phi+ b_1)+b_2)" />
            <BlockMath math="p(\text{harmful}|\phi) = 
\begin{cases}
\sigma(w_2(w_1^{T}\phi+ b_1)+b_2)  & \text{if } w_1^{T}\phi+ b_1 > 0 \\
\sigma(b_2)  & \text{else}
\end{cases}"/>
            <p>Because <InlineMath math="\text{ReLU}" /> is a piecewise function, our set becomes the union of two sets.</p>
            <BlockMath math="\mathcal{A} = \{\phi : w_1^{T}\phi + b_1 > 0 \wedge w_1^{T}\phi >  -\ln(\frac{1}{\tau_{\text{unsafe}}} - 1) - b_2 - w_2b_1 \} \cup \{\phi : w_1^{T}\phi + b_1 \leq 0 \wedge \sigma(b_2) > \tau_{\text{unsafe}} \}  " />
            <p>
                In this particular case because ReLU is monotonic and sigmoid is monotonic and
                we are looking for a threshold of the output of sigmoid, the sets meet and the boundary is linear again. Grey is the
                output of the neural network before it goes through the sigmoid. Blue and red are the safe and avoid set respectively.
            </p>
            <iframe
                src="https://www.desmos.com/3d/liczouqyv2?embed"
                width="100%"
                height="700px"
                style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                sandbox="allow-scripts allow-same-origin"
                title='desmos 1d relu'
            ></iframe>

            <p>
                With more neurons the resulting union can be more complicated.
            </p>
            <iframe
                src="https://www.desmos.com/3d/pjcmy6wpkw?embed"
                width="100%"
                height="700px"
                style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                sandbox="allow-scripts allow-same-origin"
                title='desmos 2d relu'
            ></iframe>
            <p>
                Or even disjoint.
            </p>
            <iframe
                src="https://www.desmos.com/3d/aleahwouoj?embed"
                width="100%"
                height="700px"
                style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                sandbox="allow-scripts allow-same-origin"
                title='desmos 3d relu'
            ></iframe>

            <p>
                The number of sets can be exponential in the number of hidden neurons (the number of times you perform ReLU). This problem is known as Neural Network Backwards Reachability. Recent work has shown
                that moderately-sized neural networks can be verified.<Footnote id="reach" f={f} /> <Footnote id="reach2" f={f} />
            </p>
            <p>
                This calculation can be done offline, but using this as a classifier requires more expensive online computations compared to a linear classifier.
                It's a tradeoff of getting a better classifier at the expense of more computation.
            </p>
            <p>
                The hope is that if a linear classifier works well, a small ReLU network would strike a great balance of
                predictive power and low computational demands.
            </p>
            <h4>Steering with ReLU Neural Networks</h4>
            <p>
                Finding the minimum change in this case is harder. This problem is non-convex
            </p>
            <BlockMath math="\bold{u}^{*}_{\text{change}} = \min_{\bold{u}_{\text{change}}} \|\bold{u}_{\text{change}}\|_2 \\ s.t. \ \phi + \bold{u}_{\text{change}}\notin \mathcal{A}" />
            <p>
                Let's call the subsets we are unioning to make the Avoid Set <InlineMath math="\mathcal{A}=\mathcal{A}_1 \cup \mathcal{A}_2 ... \cup \mathcal{A}_m" />.
                Note that each subset <InlineMath math="\mathcal{A}_i" /> is of the
                form <InlineMath math="\mathcal{A}_i=\{\phi : a_1^{T}\phi > c_1 \wedge a_2^{T}\phi > c_2 ... a_m^{T}\phi > c_m \}" /> So they can be rewritten as
            </p>
            <BlockMath math="\mathcal{A}_i=\{\phi : \begin{bmatrix} a_1^{T} \\ ... \\ a_n^{T} \end{bmatrix}\phi > \begin{bmatrix} c_1 \\ ... \\ c_n \end{bmatrix} \}" />
            <p>
                Which is the form of a convex polyhedron (slightly different from a convex polytope because it may be open)<Footnote id="Polyhedra" f={f} />. If <InlineMath math="\phi" /> is inside
                of a convex polyhedron <InlineMath math="\mathcal{A}_i" />, the closest point of <InlineMath math="\phi" /> to the
                boundary of <InlineMath math="\mathcal{A}_i" /> is the closest point of the projections of <InlineMath math="\phi" /> onto the hyperplanes that
                define <InlineMath math="\mathcal{A}_i" />.
            </p>
            <BlockMath math="\arg \min_{x \in \partial \mathcal{A}_i} \|x - \phi \|_{2} = \arg \min_{x \in \{ \text{proj}_{\{ x : a_{1}^Tx = c_1\}}\phi \ ... \ \text{proj}_{\{ x : a_{m}^Tx = c_m\}}\phi \} } \|x - \phi \|_{2}" />
            <p>
                For a quick mathematical argument consider that because the polyhedra is convex, 
                the planes defining it will never cross into the interior of the set.
                Claim: The closest projection of <InlineMath math="\phi"/> onto the planes must lie on the boundary that 
                is <InlineMath math="\arg\min_{x \in \{ \text{proj}_{\{ x : a_{1}^Tx = c_1\}}\phi \ ... \ \text{proj}_{\{ x : a_{m}^Tx = c_m\}}\phi \} } \|x - \phi \|_{2} = y \in \partial \mathcal{A}_i"/>.
                We can prove this claim via proof by contradiction. Assume it didn't, we could draw a line from 
                the <InlineMath math="\phi"/> through a point on the boundary <InlineMath math="y'"/> 
                to the projected point <InlineMath math="y"/>. 
                The point on the boundary <InlineMath math="y'"/> must be closer to <InlineMath math="\phi"/> than <InlineMath math="\phi"/> is
                to <InlineMath math="y"/>. Therefore <InlineMath math="y"/> is not the projection and we arrive at a contradiction.
            </p>
            <p>
                Since the union of the planes is a superset of the boundary and the closest projection onto the planes must lie on the boundary, the two optimization problems have the same solution.
            </p>
            <p>
                There's three cases for steering <InlineMath math="\phi" />
            </p>
            <ol>
                <li>
                    If <InlineMath math="\phi" /> is not inside of <InlineMath math="\mathcal{A}" /> then no adjustment needs to be made.
                </li>
                <li>
                    If <InlineMath math="\phi" /> is inside of <InlineMath math="\mathcal{A}_i" /> that is disjoint
                    from the rest of the Avoid Set <InlineMath math="\mathcal{A}_i \cap \bigcup_{j\ne i} \mathcal{A}_j= \emptyset" /> we can use
                    the technique I just described.
                </li>
                <li>
                    If <InlineMath math="\phi" /> is inside of <InlineMath math="\mathcal{A}_i" /> that intersects with other
                    subsets, we need to solve a harder problem. The union of convex polyhedra can form a non-convex set. We 
                    have to consider where the planes defining the boundary intersect
                    at lower-dimensional ridges and points because the solution may lie on one. But there are techniques for solving it.<Footnote id="geocert" f={f} />
                </li>
            </ol>
            <p>
                Even in the worst case, we still have an analytical solution so we can still take gradients through it. Nice!
            </p>
            <p>
                Again the aim isn't to solve this problem for huge neural networks but rather to improve our linear classifier (which already performs 
                well) by adding a small number of ReLU neurons such that we balance predictive power with low computational costs.
            </p>
            <h4>Non-Deterministic Problems</h4>
            <p>
                When I sampled from my LLM to generate responses, I used the maximum probability token every time so my responses were deterministic.
                In a real production system the final layer's logits will create a distribution over tokens. This makes predicting whether an output will
                be safe based on its activations harder and another reason I would recommend a final pass of the output safety
                classifier <InlineMath math="f_{\text{safety}}" /> before showing the output to the user.
            </p>
            <p>
                For a real production system, it's worth sampling multiple times to generate the training data for the probes.
            </p>
            <h3>Variations & Future Work</h3>
            <h4>Variation 1 Probe and Adjust in Real-Time</h4>
            <p>
                Rather then evaluating the entire LLM and checking if the output is unsafe, we could steer on every response.
            </p>
            <h4>Variation 2 Use a Different Probe For Classification vs Projection</h4>
            <p>
                We might prefer a simpler probe for the first stage of our safety filter but a more complicated one for projecting onto the boundary with Minimum Safe Steer.
            </p>
            <h4>Variation 3 Project further into the Safe Set</h4>
            <p>
                Right now we are projecting onto the boundary and I found that using a really low threshold worked best to encourage the output to be safe.
                We could also just project further in the set by scaling our vector slightly.
            </p>
            <h4>Variation 4 Ensembles</h4>
            <p>As I demonstrated, if the point is inside a single convex polyhedra, the problem can be solved more
                efficiently than if it is inside the union of many convex polyhedra that form a nonconvex shape. </p>
            <p>
                Is it possible to use an ensemble to classify the Avoid Set such that each model's Avoid Set is disjoint from the others?
            </p>
            <h4>Variation 5 Probe & Steer More Layers</h4>
            <p>
                In my math and code so far I have assumed we only probe one layer and therefore only steer one layer. In Anthropic's
                safety classifier paper<Footnote id="classifiers++" f={f} /> they also try probing multiple layers. Note that when steering multiple layers
                you have to steer the early layers and then steer later layers so they are affected by the steering of the earlier layer.
            </p>
            <h4>Variation 6 Sliding Windows & More</h4>
            <p>
                The linear probe used by Anthropic<Footnote id="classifiers++" f={f} /> uses a sliding window over tokens.
                It's not obvious to me that using a linear probe trained with a sliding window to project a single activation would work well. It's worth
                exploring more.
            </p>
            <h4>Variation 7 Use Probe as Feedback in Post-Training</h4>
            <p>
                When doing safety post-training, a response must be generated and a safety reward model has to be evaluated at least once for
                every response.<Footnote id="RLHF_Book" f={f} />
                If you use fine-grained rewards, it must be evaluated multiple times.<Footnote id="finegrained" f={f} /> In both cases this can be expensive. You could instead use a linear
                probe or some simple ReLU probe as a cheap reward model.
            </p>
            <h4>Future Work</h4>
            <p>
                Given a safety classifier, how well does Minimum Safe Steer perform on its false positives? 
                Does changing the classifier change the distribution of false positives in a way that 
                affects Minimum Safe Steer performance?
                What threshold should we use for <InlineMath math="\tau_{\text{unsafe}}"/>?
                How much does Minimum Safe Steer typically change the activation? 
                In real systems, the sampled output is non-deterministic. How do we train our classifier to 
                be conservative in the face of non-determinism? Perhaps we only use deterministic output when using Minimum Safe Steer.
                Do users prefer responses that Minimum Safe Steer creates over static terms of service violation messages?
            </p>
            <section id="footnotes">
                {references.map((item, i) => (
                    <FootnoteRef id={item.id} text={item.text} ref={item.ref} number={i + 1} />
                ))
                }
            </section>
        </main>
    )
};
